import { createServerFn } from "@tanstack/react-start";
import { publicSupabase } from "@/lib/supabase-public";
import { z } from "zod";
import { requireAuth as requireSupabaseAuth } from "@/lib/require-auth";
import type { Database } from "@/integrations/supabase/types";
import { cached, invalidateCache, SETTINGS_CACHE_KEY } from "@/lib/ttl-cache";


export type SiteSettings = {
  rating: number;
  reviewCount: number;
  updatedAt: string | null;
  businessName: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  textPhone: string;
  emailContact: string;
  facebookUrl: string;
  instagramUrl: string;
  youtubeUrl: string;
  linkedinUrl: string;
  ga4MeasurementId: string;
  gscVerificationToken: string;
  ga4ConnectedAt: string | null;
  gscConnectedAt: string | null;
};

const DEFAULTS: SiteSettings = {
  rating: 4.8,
  reviewCount: 84,
  updatedAt: null,
  businessName: "Durafoam Roofing LLC",
  street: "2810 E Mohawk Ln",
  city: "Phoenix",
  state: "AZ",
  postalCode: "85050",
  phone: "480-941-5373",
  textPhone: "602-456-0318",
  emailContact: "quality@durafoaminc.com",
  facebookUrl: "https://www.facebook.com/durafoamroofing",
  instagramUrl: "https://www.instagram.com/durafoamroofing",
  youtubeUrl: "https://www.youtube.com/@DurafoamRoofing",
  linkedinUrl: "https://www.linkedin.com/company/durafoam-roofing/",
  ga4MeasurementId: "",
  gscVerificationToken: "",
  ga4ConnectedAt: null,
  gscConnectedAt: null,
};

function rowToSettings(row: Record<string, unknown> | null): SiteSettings {
  if (!row) return DEFAULTS;
  return {
    rating: Number(row.google_rating ?? DEFAULTS.rating),
    reviewCount: Number(row.google_review_count ?? DEFAULTS.reviewCount),
    updatedAt: (row.updated_at as string | null) ?? null,
    businessName: (row.business_name as string | null) ?? DEFAULTS.businessName,
    street: (row.street as string | null) ?? DEFAULTS.street,
    city: (row.city as string | null) ?? DEFAULTS.city,
    state: (row.state as string | null) ?? DEFAULTS.state,
    postalCode: (row.postal_code as string | null) ?? DEFAULTS.postalCode,
    phone: (row.phone as string | null) ?? DEFAULTS.phone,
    textPhone: (row.text_phone as string | null) ?? DEFAULTS.textPhone,
    emailContact: (row.email_contact as string | null) ?? DEFAULTS.emailContact,
    facebookUrl: (row.facebook_url as string | null) ?? DEFAULTS.facebookUrl,
    instagramUrl: (row.instagram_url as string | null) ?? DEFAULTS.instagramUrl,
    youtubeUrl: (row.youtube_url as string | null) ?? DEFAULTS.youtubeUrl,
    linkedinUrl: (row.linkedin_url as string | null) ?? DEFAULTS.linkedinUrl,
    ga4MeasurementId: (row.ga4_measurement_id as string | null) ?? "",
    gscVerificationToken: (row.gsc_verification_token as string | null) ?? "",
    ga4ConnectedAt: (row.ga4_connected_at as string | null) ?? null,
    gscConnectedAt: (row.gsc_connected_at as string | null) ?? null,
  };
}

const SELECT_COLS =
  "google_rating, google_review_count, updated_at, business_name, street, city, state, postal_code, phone, text_phone, email_contact, facebook_url, instagram_url, youtube_url, linkedin_url, ga4_measurement_id, gsc_verification_token, ga4_connected_at, gsc_connected_at";
/** Public read (no auth). Used by route loaders for SSR + JSON-LD. */
export const getSiteSettings = createServerFn({ method: "GET" }).handler(
  async (): Promise<SiteSettings> =>
    cached(SETTINGS_CACHE_KEY, 60_000, async () => {
      const url = process.env.SUPABASE_URL;
      const key = process.env.SUPABASE_PUBLISHABLE_KEY;
      if (!url || !key) return DEFAULTS;
      const supabase = (await publicSupabase())!;
      const { data, error } = await supabase
        .from("site_settings")
        .select(SELECT_COLS)
        .eq("id", 1)
        .maybeSingle();
      if (error || !data) return DEFAULTS;
      return rowToSettings(data as Record<string, unknown>);
    }),
);


const updateSchema = z.object({
  rating: z.number().min(0).max(5).optional(),
  reviewCount: z.number().int().min(0).max(1_000_000).optional(),
  businessName: z.string().max(120).optional(),
  street: z.string().max(200).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(40).optional(),
  postalCode: z.string().max(20).optional(),
  phone: z.string().max(40).optional(),
  textPhone: z.string().max(40).optional(),
  emailContact: z.string().max(200).optional(),
  facebookUrl: z.string().max(300).optional(),
  instagramUrl: z.string().max(300).optional(),
  youtubeUrl: z.string().max(300).optional(),
  linkedinUrl: z.string().max(300).optional(),
  ga4MeasurementId: z.string().max(40).optional(),
  gscVerificationToken: z.string().max(200).optional(),
});

/** Admin-only write. */
export const updateSiteSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => updateSchema.parse(input))
  .handler(async ({ data, context }): Promise<SiteSettings> => {
    const { supabase, userId } = context;
    const { data: isAdmin, error: roleErr } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    if (roleErr || !isAdmin) throw new Error("Forbidden: admin role required");

    // Load current row to detect connect timestamps for GA4/GSC
    const { data: current } = await supabase
      .from("site_settings")
      .select("ga4_measurement_id, ga4_connected_at, gsc_verification_token, gsc_connected_at")
      .eq("id", 1)
      .maybeSingle();

    const now = new Date().toISOString();
    const patch: Record<string, unknown> = {
      updated_at: now,
      updated_by: userId,
    };
    if (data.rating !== undefined) patch.google_rating = Math.round(data.rating * 10) / 10;
    if (data.reviewCount !== undefined) patch.google_review_count = data.reviewCount;
    if (data.businessName !== undefined) patch.business_name = data.businessName;
    if (data.street !== undefined) patch.street = data.street;
    if (data.city !== undefined) patch.city = data.city;
    if (data.state !== undefined) patch.state = data.state;
    if (data.postalCode !== undefined) patch.postal_code = data.postalCode;
    if (data.phone !== undefined) patch.phone = data.phone;
    if (data.textPhone !== undefined) patch.text_phone = data.textPhone;
    if (data.emailContact !== undefined) patch.email_contact = data.emailContact;
    if (data.facebookUrl !== undefined) patch.facebook_url = data.facebookUrl;
    if (data.instagramUrl !== undefined) patch.instagram_url = data.instagramUrl;
    if (data.youtubeUrl !== undefined) patch.youtube_url = data.youtubeUrl;
    if (data.linkedinUrl !== undefined) patch.linkedin_url = data.linkedinUrl;

    if (data.ga4MeasurementId !== undefined) {
      const trimmed = data.ga4MeasurementId.trim();
      patch.ga4_measurement_id = trimmed || null;
      const wasEmpty = !current?.ga4_measurement_id;
      patch.ga4_connected_at = trimmed ? (wasEmpty ? now : current?.ga4_connected_at ?? now) : null;
    }
    if (data.gscVerificationToken !== undefined) {
      const trimmed = data.gscVerificationToken.trim();
      patch.gsc_verification_token = trimmed || null;
      const wasEmpty = !current?.gsc_verification_token;
      patch.gsc_connected_at = trimmed ? (wasEmpty ? now : current?.gsc_connected_at ?? now) : null;
    }

    const { data: updated, error } = await supabase
      .from("site_settings")
      .update(patch as never)
      .eq("id", 1)
      .select(SELECT_COLS)
      .single();
    if (error || !updated) throw new Error(error?.message ?? "Update failed");
    invalidateCache(SETTINGS_CACHE_KEY);
    return rowToSettings(updated as Record<string, unknown>);

  });

/** Check whether current user has admin role. */
export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ isAdmin: boolean; userId: string }> => {
    const { supabase, userId } = context;
    const { data, error } = await supabase.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });
    if (error) return { isAdmin: false, userId };
    return { isAdmin: Boolean(data), userId };
  });
